import { Icon } from "@mdi/react";
import { useCallback, useEffect, useRef, useState } from "react";

import { mdiAccount, mdiAccountMultiple, mdiCheck, mdiClose, mdiDomain } from "@mdi/js";
import { Button, Input, Select } from "antd";
import styled from "styled-components";

import { ProjectWithPermission } from "models/projects";

import { Tooltip } from "components/base";
import { useSelectedProject } from "components/project/projects/hooks";

const { Option } = Select;

interface TemplateSaveAsProps {
  isOpen: boolean;
  selectedTemplateName: string;
  editableProjects: ProjectWithPermission[];
  onSave: (name: string, projectId: string) => void;
  onCancel: () => void;
}

export function TemplateSaveAs({
  isOpen,
  selectedTemplateName,
  editableProjects,
  onSave,
  onCancel
}: TemplateSaveAsProps) {
  const { selectedProject } = useSelectedProject();

  const [saveAsTitle, setSaveAsTitle] = useState("");
  const [saveAsToProjectId, setSaveAsToProjectId] = useState<string>("");
  const [nameError, setNameError] = useState("");

  const saveAsInputRef = useRef<HTMLInputElement>(null);

  // Initialize title and error when the popover opens
  useEffect(() => {
    if (isOpen) {
      setSaveAsTitle(selectedTemplateName ? `${selectedTemplateName} Copy` : "");
      setNameError("");
      requestAnimationFrame(() => saveAsInputRef.current?.select());
    }
  }, [isOpen, selectedTemplateName]);

  // Sync project selection: default to the current project; fall back to the first
  // editable project only when the current project isn't in the editable list.
  // Runs on open, project change, and when the editable project list changes.
  // Does NOT depend on saveAsToProjectId so it never overrides a user's manual selection.
  useEffect(() => {
    if (!isOpen) return;
    const currentProjectId = selectedProject?.projectId || "";
    if (!editableProjects.length) {
      setSaveAsToProjectId(currentProjectId);
      return;
    }
    const isCurrentEditable = editableProjects.some(
      (p) => p.projectId === currentProjectId
    );
    setSaveAsToProjectId(
      isCurrentEditable ? currentProjectId : editableProjects[0].projectId
    );
  }, [isOpen, selectedProject?.projectId, editableProjects]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = useCallback(() => {
    const trimmed = saveAsTitle.trim();
    if (!trimmed) {
      setNameError("Name is required");
      return;
    }
    if (!saveAsToProjectId) {
      setNameError("Select a project");
      return;
    }
    onSave(trimmed, saveAsToProjectId);
    onCancel();
  }, [saveAsTitle, saveAsToProjectId, onSave, onCancel]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancel = useCallback(() => {
    setNameError("");
    onCancel();
  }, [onCancel]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSaveAsTitle(e.target.value);
    setNameError("");
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") handleSave();
      else if (e.key === "Escape") handleCancel();
    },
    [handleSave, handleCancel]
  );

  return (
    <SaveAsWrapper>
      <InputWrapper $hasError={!!nameError}>
        <SaveAsInput
          ref={saveAsInputRef}
          value={saveAsTitle}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Enter template name"
          status={nameError ? "error" : ""}
        />
        {nameError && <ErrorMessage>{nameError}</ErrorMessage>}
      </InputWrapper>
      <Select
        value={saveAsToProjectId}
        onChange={(val) => setSaveAsToProjectId(val)}
        placeholder="Select project">
        {editableProjects.map((project) => (
          <Option
            key={project.projectId}
            value={project.projectId}
            label={project.projectName}>
            <OptionWrapper>
              <span role="img">
                {project.projectType === "Personal" ? (
                  <Icon path={mdiAccount} size={1.1} />
                ) : project.projectType === "Shared" ? (
                  <Icon path={mdiAccountMultiple} size={1.1} />
                ) : (
                  <Icon path={mdiDomain} size={1.1} />
                )}
              </span>
              {project.projectName}
            </OptionWrapper>
          </Option>
        ))}
      </Select>
      <Tooltip title="Cancel">
        <Button
          icon={<Icon path={mdiClose} size={1} color="#bbbbbb" />}
          type="text"
          onClick={handleCancel}
        />
      </Tooltip>
      <Tooltip title="Save As" destroyTooltipOnHide>
        <Button
          icon={<Icon path={mdiCheck} size={1} color="#02c7b0" />}
          type="text"
          disabled={!saveAsTitle.trim() || !saveAsToProjectId}
          onClick={handleSave}
        />
      </Tooltip>
    </SaveAsWrapper>
  );
}

const SaveAsWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  vertical-align: middle;
  width: 100%;
  gap: 5px;
  overflow: hidden;
  padding-left: 2px;

  .ant-btn-icon-only {
    width: 24px;
    height: 24px;
    margin-top: 0;
  }

  .ant-select-selector {
    max-width: 125px;
    min-width: 125px;
  }
`;

const InputWrapper = styled.div<{ $hasError: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: ${({ $hasError }) => ($hasError ? "48px" : "auto")};
`;

const SaveAsInput = styled(Input)`
  height: 25px;
  border: 1px solid black;
  background-color: white;
  max-width: 330px;
  min-width: 200px;
  margin-top: 3px;
`;

const ErrorMessage = styled.div`
  color: var(--color-danger, #ff4d4f);
  font-size: 12px;
  font-weight: var(--fontWeightMedium);
  margin-top: 2px;
`;

const OptionWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;

  span {
    padding: 2px 5px 0;
  }
`;

export default TemplateSaveAs;
